import { css, cx } from "@emotion/css";
import { borderRadius, space, themeTokens, text, borderWidth } from "@octopusdeploy/design-system-tokens";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import { resetStyles } from "../../utils/resetStyles";
import type { ButtonImportance, ButtonSize } from "./Button";
export function getButtonStyles(importance: ButtonImportance, disabled: boolean, size: ButtonSize, hasLabel: boolean) {
    const buttonVariantStyles = getButtonVariantStyles(importance, disabled, size);
    return cx(baseButtonStyles, buttonVariantStyles, { [buttonWithLabelStyles]: hasLabel });
}
const baseButtonStyles = css({
    ...resetStyles.button,
    font: text.body.bold.large,
    width: "fit-content",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transitionTimingFunction: "ease-in-out",
    transitionProperty: "background-color, border-color, outline, color, fill",
    transitionDuration: "150ms",
    borderRadius: borderRadius.small,
    whiteSpace: "nowrap",
});
const buttonWithLabelStyles = css({
    minWidth: "5rem",
});
function getButtonVariantStyles(importance: ButtonImportance, disabled: boolean, size: ButtonSize): string {
    if (disabled) {
        const isFilled = importance !== "quiet" && importance !== "ghost";
        const background = isFilled ? themeTokens.color.button.background.disabled : undefined;
        return css({
            color: themeTokens.color.button.text.disabled,
            background,
            font: size === "medium" ? text.body.bold.medium : text.body.bold.small,
            border: isFilled ? `solid ${buttonBorderWidth} ${themeTokens.color.button.border.disabled}` : undefined,
            padding: getButtonPadding(size, isFilled),
            cursor: "not-allowed",
            svg: {
                fill: themeTokens.color.button.icon.disabled,
            },
            ":hover": {
                background,
                color: themeTokens.color.button.text.disabled, // ensures that global styling from consumers doesn't change the text color
            },
        });
    }
    if (importance === "ghost") {
        const baseColor = "currentcolor";
        return css({
            color: baseColor,
            background: "transparent",
            font: size === "medium" ? text.body.bold.medium : text.body.bold.small,
            padding: getButtonPadding(size, false),
            svg: {
                fill: baseColor,
            },
            ":hover, :focus-visible": {
                background: `rgb(from ${baseColor} r g b / 0.08)`,
            },
            ":active, &[aria-expanded='true'][aria-haspopup='menu']": {
                background: `rgb(from ${baseColor} r g b / 0.24)`,
            },
        });
    }
    const importanceLookup = getImportanceLookupValue(importance);
    const backgroundTokens = themeTokens.color.button.background[importanceLookup];
    const borderColor = getBorderTokens(importanceLookup);
    const textColor = themeTokens.color.button.text[importanceLookup];
    const iconColor = themeTokens.color.button.icon[importanceLookup];
    const padding = getButtonPadding(size, borderColor !== undefined);
    return css({
        color: textColor,
        background: backgroundTokens.default,
        border: borderColor ? `solid ${buttonBorderWidth} ${borderColor}` : undefined,
        font: size === "medium" ? text.body.bold.medium : text.body.bold.small,
        // we need to subtract the border width for the button to be the correct height (40px, not 42px)
        padding,
        svg: {
            fill: iconColor,
        },
        ":hover": {
            background: backgroundTokens.hover,
            color: textColor, // ensures that global styling from consumers doesn't change the text color
        },
        ":focus-visible": {
            background: backgroundTokens.hover,
            color: textColor, // ensures that global styling from consumers doesn't change the text color
            outline: `solid ${borderWidth[2]} ${themeTokens.color.border.selected}`,
            outlineOffset: borderWidth[2],
        },
        ":active, &[aria-expanded='true'][aria-haspopup='menu']": {
            background: backgroundTokens.pressed,
            color: textColor, // ensures that global styling from consumers doesn't change the text color
        },
    });
}
function getButtonPadding(size: ButtonSize, hasBorder: boolean) {
    const baseVerticalPaddingSize = size === "medium" ? space[8] : space[6];
    const baseHorizontalPaddingSize = space[6];
    const borderWidth = hasBorder ? buttonBorderWidth : "0px";
    const padding = `calc(${baseVerticalPaddingSize} - ${borderWidth}) calc(${baseHorizontalPaddingSize} - ${borderWidth})`;
    return padding;
}
function getBorderTokens(importance: TokenButtonImportance) {
    switch (importance) {
        case "primary":
            return undefined;
        case "secondary":
            return themeTokens.color.button.border.secondary;
        case "tertiary":
            return undefined;
        case "destructive":
            return undefined;
        case "quiet":
            return undefined;
        case "loud":
            return undefined;
        case "neutral":
            return undefined;
        default:
            exhaustiveCheck(importance, `Could not find lookup value for unknown variant: ${importance}`);
    }
}
/**
 * @param importance "ghost" is excluded here since it has no tokens and is handled separately
 */
function getImportanceLookupValue(importance: Exclude<ButtonImportance, "ghost">): TokenButtonImportance {
    switch (importance) {
        case "primary":
            return "primary";
        case "secondary":
            return "secondary";
        case "tertiary":
            return "tertiary";
        case "destructive":
            return "destructive";
        case "quiet":
            return "quiet";
        case "loud":
            return "loud";
        case "neutral":
            return "neutral";
        default:
            exhaustiveCheck(importance, `Could not find lookup value for unknown variant: ${importance}`);
    }
}
export function getButtonIconStyles(size: ButtonSize) {
    return size === "medium" ? buttonIconStyles : undefined;
}
export const buttonLabelStyles = css({
    marginRight: space[4],
    marginLeft: space[4],
});
const buttonIconStyles = css({
    marginRight: space[2],
    marginLeft: space[2],
});
// Our tokens include the "disabled" as a type of button, at the same level as "primary", "secondary" etc
// This differs to the Button component API where disabled is a prop separate to the importance.
// In these mappings, we treat manually handle the disabled state so we exclude it from this type.
type TokenButtonImportance = Exclude<keyof typeof themeTokens.color.button.background, "disabled">;
const buttonBorderWidth = "1px";
